import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Divider, Grid, Paper, Tooltip } from '@mui/material';
import { Add, ArrowBack, SwapVert } from '@mui/icons-material';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { Schema } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import { SecondaryTitle } from 'src/shared/components/atoms/secondaryTitle';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import DataGridWrapper from 'src/shared/components/organisms/dataGridWrapper';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ControlledSelectBox from 'src/shared/components/form/controlledSelectBox';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import { HelpPageCategory } from '../models/helpPageCategory';
import { HelpPageModule } from '../models/helpPageModule';
import { HelpPageSearchFilter } from '../models/helpPageSearchFilter';
import {
  getAllHelpPageCategories,
  createHelpPageCategory,
  updateHelpPageCategory,
  deleteHelpPageCategory,
} from '../services/helpPageCategoryService';
import { getAllHelpPageModules } from '../services/helpPageModuleService';
import { countHelpPagesInCategory } from '../services/helpPageService';
import { HelpArticlesDataGrid } from '../components/helpArticlesDataGrid';
import { HelpPageOrderDialog } from '../components/helpPageOrderDialog';
import { getCategoryIdsWithChildren } from '../functions/getCategoryOptions';

// parentId 0 används i formuläret för "ingen förälder", eftersom select-boxen inte hanterar null.
const NO_PARENT = 0;

type CategoryFormValues = {
  name: string;
  description: string;
  parentId: number;
};

export const HelpArticleCategoryEditPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['helpArticles', 'common']);
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [categories, setCategories] = useState<HelpPageCategory[]>([]);
  const [category, setCategory] = useState<HelpPageCategory | null>(null);
  const [modules, setModules] = useState<HelpPageModule[]>([]);
  const [refetchArticlesGrid, setRefetchArticlesGrid] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [openOrderDialog, setOpenOrderDialog] = useState<boolean>(false);
  // Kategorins egna artiklar som inte är arkiverade. Ordningen går bara att ändra när de är minst två.
  const [ownArticleCount, setOwnArticleCount] = useState<number>(0);
  const [articlesSearchFilter, setArticlesSearchFilter] = useState<HelpPageSearchFilter>({
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
    sortCol: 'title',
    sortDir: 'asc',
  });

  const schema: Schema<CategoryFormValues> = yup.object().shape({
    // Samma regel som API:t: namnet trimmas och får vara högst 200 tecken.
    name: yup
      .string()
      .trim()
      .max(200, t('common:validationMessages.maxLength', { count: 200 }))
      .required(t('common:validationMessages.required')),
    // Valfri. API:t trimmar och sparar en tom beskrivning som ingen.
    description: yup
      .string()
      .trim()
      .max(300, t('common:validationMessages.maxLength', { count: 300 }))
      .defined(),
    parentId: yup.number().required(),
  });

  const formMethods = useForm<CategoryFormValues>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: { name: '', description: '', parentId: NO_PARENT },
  });

  const { handleSubmit, reset } = formMethods;

  // Egen felhantering, utan felruta: utan antalet döljs bara knappen "Ändra ordning", kategorin går fortfarande att
  // redigera.
  const loadOwnArticleCount = useCallback(
    async (categoryId: number) => {
      try {
        const result = await countHelpPagesInCategory(categoryId);
        if (isMounted()) setOwnArticleCount(result.data.totalItems);
      } catch (e) {
        ConsoleHelper.log(e);
      }
    },
    [isMounted],
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAllHelpPageCategories();
      if (!isMounted()) return;

      setCategories(result.data);

      if (!id) {
        setCategory(null);
        reset({ name: '', description: '', parentId: NO_PARENT });
        return;
      }

      const found = result.data.find((c) => c.id === Number(id));
      if (!found) {
        toast.error(t('helpArticles:categories.notFound') as string);
        // Tillbaka till listan, annars visas formuläret för att skapa och Spara skapar en ny kategori.
        navigate(RoutesEnum.HelpArticleCategories, { replace: true });
        return;
      }

      setCategory(found);
      reset({ name: found.name, description: found.description ?? '', parentId: found.parentId ?? NO_PARENT });

      // För en huvudkategori visas artiklar från både den själv och alla dess underkategorier,
      // eftersom artiklar annars nästan alltid ligger på en underkategori och listan ser tom ut.
      // Visar listan bara kategorins egna artiklar sorteras den som i hjälpcentret, på platsen. Platsen gäller bara
      // inom en kategori, så med underkategorier sorteras den på titel. Sorteringen sätts bara när kategorin byts,
      // så att den som användaren valt står kvar när kategorin sparas.
      const categoryIds = getCategoryIdsWithChildren(result.data, found.id);
      setArticlesSearchFilter((prev) =>
        prev.categoryIds?.[0] === found.id
          ? { ...prev, categoryIds }
          : { ...prev, categoryIds, pageIndex: 0, sortCol: categoryIds.length > 1 ? 'title' : 'sortOrder', sortDir: 'asc' },
      );
      loadOwnArticleCount(found.id);
    } catch (e) {
      ConsoleHelper.log(e);
      // Av samma skäl: utan kategorin får redigeringen inte bli ett formulär för att skapa.
      if (id && isMounted()) navigate(RoutesEnum.HelpArticleCategories, { replace: true });
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [id, isMounted, loadOwnArticleCount, navigate, reset, t]);

  useEffect(() => {
    if (isMounted()) loadData();
  }, [isMounted, loadData]);

  useEffect(() => {
    const loadModules = async () => {
      try {
        const result = await getAllHelpPageModules();
        if (isMounted()) setModules(result.data);
      } catch (e) {
        ConsoleHelper.log(e);
      }
    };
    loadModules();
  }, [isMounted]);

  const onSubmit = async (data: CategoryFormValues) => {
    setSaving(true);
    const parentId = data.parentId === NO_PARENT ? null : Number(data.parentId);

    try {
      if (category) {
        await updateHelpPageCategory({ ...category, name: data.name, description: data.description, parentId });
        if (isMounted()) {
          toast.success(t('helpArticles:categories.updatedSuccess') as string);
          loadData();
        }
      } else {
        const created = await createHelpPageCategory({ name: data.name, description: data.description, parentId });
        if (isMounted()) {
          toast.success(t('helpArticles:categories.createdSuccess') as string);
          navigate(`${RoutesEnum.HelpArticleCategoryEdit}/${created.data.id}`, { replace: true });
        }
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!category) return;
    setSaving(true);

    try {
      await deleteHelpPageCategory(category.id);
      if (isMounted()) {
        toast.success(t('helpArticles:categories.deletedSuccess') as string);
        navigate(RoutesEnum.HelpArticleCategories);
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setSaving(false);
    }
  };

  // En kategori kan inte vara sin egen förälder.
  const mainCategories = categories.filter((c) => c.parentId === null && c.id !== category?.id);
  const hasChildren = category ? categories.some((c) => c.parentId === category.id) : false;

  const parentOptions = [
    { label: t('helpArticles:categories.noParent'), value: NO_PARENT },
    ...mainCategories.map((c) => ({ label: c.name, value: c.id })),
  ];

  const formId = 'help-page-category-form';

  return (
    <GridWrapper>
      <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <MainContentHeader title={category ? t('helpArticles:categories.editCategory') : t('helpArticles:categories.createCategory')} />
        <Button startIcon={<ArrowBack fontSize='small' />} onClick={() => navigate(RoutesEnum.HelpArticleCategories)}>
          {t('helpArticles:form.backToList')}
        </Button>
      </Grid>

      <Grid item xs={12}>
        <Paper>
          <Box className='section-inset' sx={{ p: 3 }}>
            <DataGridWrapper loading={loading}>
              <FormProvider {...formMethods}>
                <form id={formId} onSubmit={handleSubmit(onSubmit)}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <ControlledTextField label={t('helpArticles:categories.name')} name='name' />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {/* En kategori med underkategorier måste vara en huvudkategori (två nivåer). */}
                      <Tooltip title={hasChildren ? t('helpArticles:categories.hasChildrenParentTooltip') : ''}>
                        <Box>
                          <ControlledSelectBox
                            items={parentOptions}
                            name='parentId'
                            label={t('helpArticles:categories.parent')}
                            disabled={hasChildren}
                          />
                        </Box>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={12}>
                      <ControlledTextField
                        label={t('helpArticles:categories.description')}
                        name='description'
                        helperText={t('helpArticles:categories.descriptionHelp')}
                        multiline
                        minRows={2}
                      />
                    </Grid>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: category ? 'space-between' : 'flex-end', alignItems: 'center' }}>
                      {category && (
                        <Tooltip title={hasChildren ? t('helpArticles:categories.hasChildrenTooltip') : ''}>
                          <span>
                            <Button variant='outlined' color='error' disabled={saving || hasChildren} onClick={() => setOpenDeleteDialog(true)}>
                              {t('common:button.delete')}
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      <Button form={formId} type='submit' disabled={saving} disableElevation variant='contained' color='primary'>
                        {t('common:button.save')}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </FormProvider>
            </DataGridWrapper>
          </Box>
        </Paper>
      </Grid>

      {category && (
        <Grid item xs={12} sx={{ mt: 5 }}>
          <Paper>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 2 }}>
              <SecondaryTitle title={t('helpArticles:categories.relatedArticles')} uppercase />
              <Box sx={{ display: 'flex', gap: 1 }}>
                {ownArticleCount >= 2 && (
                  <Button
                    size='small'
                    variant='outlined'
                    color='primary'
                    startIcon={<SwapVert fontSize='small' />}
                    onClick={() => setOpenOrderDialog(true)}
                  >
                    {t('helpArticles:categories.changeOrder')}
                  </Button>
                )}
                <Button
                  size='small'
                  variant='outlined'
                  color='primary'
                  startIcon={<Add fontSize='small' />}
                  onClick={() => navigate(RoutesEnum.HelpArticleCreate, { state: { categoryId: category.id } })}
                >
                  {t('helpArticles:createArticle')}
                </Button>
              </Box>
            </Box>
            <Divider />
            <Box className='section-inset'>
              {/* Ny för varje kategori, eftersom griden bara läser sorteringen när den skapas. */}
              <HelpArticlesDataGrid
                key={category.id}
                searchFilter={articlesSearchFilter}
                searchFilterCallback={(newFilter) => setArticlesSearchFilter((prev) => ({ ...prev, ...newFilter }))}
                categories={categories}
                modules={modules}
                refetchDataGrid={refetchArticlesGrid}
                setRefetchDataGrid={setRefetchArticlesGrid}
                hideCategoryColumn={(articlesSearchFilter.categoryIds?.length ?? 1) <= 1}
                showSortOrderColumn={(articlesSearchFilter.categoryIds?.length ?? 1) <= 1}
                onSelectHelpPage={(helpPage) => navigate(`${RoutesEnum.HelpArticleEdit}/${helpPage.id}`)}
              />
            </Box>
          </Paper>
        </Grid>
      )}

      {category && (
        <HelpPageOrderDialog
          open={openOrderDialog}
          categoryId={category.id}
          categoryName={category.name}
          onClose={(saved) => {
            setOpenOrderDialog(false);
            if (saved) setRefetchArticlesGrid(true);
          }}
        />
      )}

      <DeleteConfirmationDialog
        title={t('common:confirmDelete')}
        value={category?.name ?? ''}
        modaltext={t('common:irreversibleAction')}
        onClose={(value) => {
          // Dialogen stängs direkt, så att OK inte kan ge en andra radering medan anropet pågår.
          setOpenDeleteDialog(false);
          if (value) handleDelete();
        }}
        open={openDeleteDialog}
      />
    </GridWrapper>
  );
};
